import { injectable, interfaces } from 'inversify';
import { action, makeObservable, observable } from 'mobx';
import { ImportAnnotationConflict, ResolveConflictDecision } from './models/ImportAnnotationsConflicts';
import { IImportAnnotationsReport } from './models/ImportAnnotationsReport';
import { ImportAnnotationsStep } from './models/ImportAnnotationsSteps';
import { ResolveConflictsStrategy } from './models/ResolveConflictsStrategy';

export interface IImportAnnotationsStore {
  readonly currentStep: ImportAnnotationsStep;
  readonly file: File | undefined;
  readonly fileName: string | undefined;
  readonly fileValidationError: string | undefined;
  readonly conflicts: ImportAnnotationConflict[];
  readonly projectId: string | undefined;
  readonly resolveConflictsStrategy: ResolveConflictsStrategy;
  readonly processing: boolean;
  readonly report: IImportAnnotationsReport | undefined;
  readonly failureCodes: string[];
  setCurrentStep(step: ImportAnnotationsStep): void;
  cleanFileInfo(): void;
  setProjectId(projectId: string): void;
  setConflictsResults(conflicts: ImportAnnotationConflict[], file: File): void;
  setFileValidationError(error: string): void;
  setResolveConflictsStrategy(strategy: ResolveConflictsStrategy): void;
  setProcessing(processing: boolean): void;
  setImportAnnotationsFinished(report: IImportAnnotationsReport): void;
  setImportAnnotationsFailed(errorCodes: string[]): void;
  clean(): void;
  setAllConflictsDecisions(decision: ResolveConflictDecision): void;
  setConflictDecision(conflict: ImportAnnotationConflict): void;
}

export namespace IImportAnnotationsStore {
  export const $: interfaces.ServiceIdentifier<IImportAnnotationsStore> = Symbol('IMPORT_ANNOTATIONS_STORE');
}

@injectable()
export class ImportAnnotationsStoreImpl implements IImportAnnotationsStore {
  currentStep: ImportAnnotationsStep = ImportAnnotationsStep.None;

  failureCodes: string[] = [];

  file: File | undefined = undefined;

  fileName: string | undefined;

  conflicts: ImportAnnotationConflict[] = [];

  fileValidationError: string | undefined;

  projectId: string | undefined;

  processing: boolean = false;

  resolveConflictsStrategy: ResolveConflictsStrategy = ResolveConflictsStrategy.KeepAll;

  report: IImportAnnotationsReport | undefined;

  constructor() {
    makeObservable(this, {
      currentStep: observable,
      failureCodes: observable,
      file: observable,
      fileName: observable,
      conflicts: observable,
      fileValidationError: observable,
      projectId: observable,
      processing: observable,
      resolveConflictsStrategy: observable,
      report: observable,
      setCurrentStep: action,
      setProjectId: action,
      setFileValidationError: action,
      setConflictsResults: action,
      setResolveConflictsStrategy: action,
      setProcessing: action,
      setImportAnnotationsFailed: action,
      setImportAnnotationsFinished: action,
      cleanFileInfo: action,
      clean: action,
      setAllConflictsDecisions: action,
      setConflictDecision: action,
    });
  }

  setCurrentStep(step: ImportAnnotationsStep): void {
    this.currentStep = step;
  }

  setProjectId(projectId: string): void {
    this.projectId = projectId;
  }

  setFileValidationError(error: string): void {
    this.fileValidationError = error;
  }

  setConflictsResults(conflicts: ImportAnnotationConflict[], file: File): void {
    this.conflicts = conflicts;
    this.fileName = file.name;
    this.file = file;
  }

  setResolveConflictsStrategy(strategy: ResolveConflictsStrategy): void {
    this.resolveConflictsStrategy = strategy;
  }

  setProcessing(processing: boolean): void {
    this.processing = processing;
  }

  setImportAnnotationsFailed(errorCodes: string[]): void {
    this.processing = false;
    this.failureCodes = errorCodes;
    this.currentStep = ImportAnnotationsStep.Summary;
  }

  setImportAnnotationsFinished(report: IImportAnnotationsReport): void {
    this.report = report;
    this.processing = false;
    this.currentStep = ImportAnnotationsStep.Summary;
  }

  cleanFileInfo(): void {
    this.fileValidationError = undefined;
    this.fileName = undefined;
    this.conflicts = [];
    this.file = undefined;
  }

  clean(): void {
    this.fileName = undefined;
    this.projectId = undefined;
    this.conflicts = [];
    this.processing = false;
    this.fileValidationError = undefined;
    this.file = undefined;
    this.report = undefined;
    this.failureCodes = [];
    this.currentStep = ImportAnnotationsStep.None;
  }

  setAllConflictsDecisions(decision: ResolveConflictDecision): void {
    this.conflicts.forEach((c) => (c.decision = decision));
  }

  setConflictDecision(conflict: ImportAnnotationConflict): void {
    const oldConflict = this.conflicts.find((c) => c.dataSetName === conflict.dataSetName && c.imageName === conflict.imageName);
    if (oldConflict) {
      oldConflict.decision = conflict.decision;
    }
  }
}
